// Review and Confirmation Step
import React from 'react';
import { CheckCircle, Edit, User, FileText, Briefcase, Settings } from 'lucide-react';
import { ResumeData } from '../../types';

interface ReviewStepProps {
  data: {
    jobTitle: string;
    resumeFile: File | null;
    resumeData: ResumeData | null;
    jobDescription: string;
    systemPrompt: string;
  };
  onEdit: (step: 'job-title' | 'resume' | 'job-description' | 'system-prompt') => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  data,
  onEdit
}) => {
  const { jobTitle, jobDescription, systemPrompt } = data;

  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-success-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Review Your Setup
        </h2>
        <p className="text-gray-600">
          Please review your information before completing the setup. You can edit any section if needed.
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-4">
        {/* Job Title */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <Briefcase className="w-5 h-5 text-primary-600 mr-2" />
              <h3 className="font-medium text-gray-900">Job Title</h3>
            </div>
            <button
              onClick={() => onEdit('job-title')}
              className="flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </button>
          </div>
          <p className="text-gray-700">{jobTitle}</p>
        </div>

        {/* Resume Information */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <User className="w-5 h-5 text-primary-600 mr-2" />
              <h3 className="font-medium text-gray-900">Resume Information</h3>
            </div>
            <button
              onClick={() => onEdit('resume')}
              className="flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </button>
          </div>
          
          {data.resumeFile ? (
            <p className="text-gray-700 text-sm">{data.resumeFile.name}</p>
          ) : (
            <p className="text-gray-500 text-sm">No resume uploaded</p>
          )}
        </div>

        {/* Job Description */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-primary-600 mr-2" />
              <h3 className="font-medium text-gray-900">Job Description</h3>
            </div>
            <button
              onClick={() => onEdit('job-description')}
              className="flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </button>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            {truncateText(jobDescription)}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            {jobDescription.split(' ').length} words
          </p>
        </div>

        {/* System Prompt */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <Settings className="w-5 h-5 text-primary-600 mr-2" />
              <h3 className="font-medium text-gray-900">AI Configuration</h3>
            </div>
            <button
              onClick={() => onEdit('system-prompt')}
              className="flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </button>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            {truncateText(systemPrompt)}
          </p>
        </div>
        

        {/* Summary */}
        <div className="bg-gradient-to-r from-primary-50 to-success-50 border border-primary-200 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-2">Setup Summary</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Position</p>
              <p className="font-medium text-gray-900">{jobTitle}</p>
            </div>
            <div>
              <p className="text-gray-600">Resume Status</p>
              <p className={`font-medium ${data.resumeFile ? 'text-success-700' : 'text-orange-600'}`}>
                {data.resumeFile ? 'Uploaded' : 'Not uploaded'}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Job Description</p>
              <p className="font-medium text-gray-900">{jobDescription.split(' ').length} words</p>
            </div>
            <div>
              <p className="text-gray-600">AI Configuration</p>
              <p className="font-medium text-success-700">Configured</p>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">What happens next?</h3>
          <ul className="text-blue-800 text-sm space-y-1">
            <li>• Your preferences will be saved to make your personalized AI.</li>
            <li>• Your personalizedd AI will assist you during the interview.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
